import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CATEGORIES,
  DESKS,
  answerRows,
  categoriesForDesk,
  categoryById,
  deskById,
  deskForCategory,
  fieldsFor,
  hasAnswer,
  typeById,
  typesFor,
  visibleFields,
} from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { formatTarget } from '../demo/sla.js';
import { createTicket, previewPriority, ticketNoOf } from '../demo/store.js';
import ClaudeSeatChat from './ClaudeSeatChat.jsx';
import FormWizard from './FormWizard.jsx';
import BackButton from '../components/BackButton.jsx';
import Field from '../components/Field.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { managerOf, myTeam } from '../lib/people.js';
import { offerForSomeone, personFor, wordsForFilingRefusal } from '../lib/onBehalf.js';
import { filesForReal } from '../lib/filesForReal.js';
import { labelFor } from '../lib/tone.js';
import { keyForAttempt, newDraft } from '../lib/operationKey.js';
import { sendFiles, wordsForFailed } from '../lib/ticketFiles.js';
import { FilePicker } from '../components/TicketFiles.jsx';

/* Raising a ticket. Structured only — there is no "describe your issue" box.
 *
 * The form fields ARE the triage: category · type · frequency · since ·
 * others-affected, joined to the requester's brand and role. The rules engine
 * and the queue filters read those directly. Nothing is parsed, nothing is
 * inferred, and no model runs on the way in — AI runs exactly once in a
 * ticket's life, at close, to write the summary.
 *
 * The priority readout under the form updates LIVE as answers land. That is the
 * argument for rules over a model made visible: the requester sees the priority
 * and the rule that produced it before they submit, and IT can point at the
 * same rule afterwards.
 */

/* One-tap deep links.
 *
 * `?category=` has always been supported. `?type=` was added for the technician
 * quick links (2026-08-21) so a button can land on the FORM rather than on the
 * type list — the difference between four taps and two, which is the whole
 * point of the tech home. `?desk=` was added with the three-desk split
 * (2026-09-14) so a tile on Home opens intake already inside that desk.
 *
 * All three are VALIDATED, never trusted. Anything that does not exist, or a
 * combination that disagrees with itself, is dropped and the wizard falls back
 * to the furthest step it can actually prove. A hand-edited URL must not be
 * able to build a ticket whose category and request type contradict — that
 * ticket would route to one team and prioritise as another.
 *
 * PRECEDENCE, MOST SPECIFIC WINS: a type implies its category, and a category
 * implies its desk. So `?desk=ai-access&category=phones` keeps Phones and
 * quietly corrects the desk to IT Help, rather than showing a ServiceTitan
 * category inside the AI desk. `?desk=` only decides anything when it is the
 * only thing given, which is exactly the Home-tile case.
 */
function fromDeepLink(params) {
  const categoryId = categoryById(params.get('category'))?.id || null;
  const type = typeById(params.get('type'));
  // Only consulted when no category pins it down. deskById returns null on an
  // unknown id, so a junk ?desk= lands on the browse-everything screen.
  const askedDesk = deskById(params.get('desk'));

  const deskFor = (catId) => deskForCategory(catId)?.id || askedDesk?.id || null;

  /* A DESK WITH ONE AREA IMPLIES THAT AREA.
   *
   * Found by screenshotting, not by a test — every assertion passed while the
   * AI Access desk rendered a single tile stretched across the page that you
   * had to click to reach the only list behind it. A choice of one is not a
   * choice, it is a wasted tap that looks broken.
   *
   * ServiceTitan Help is the same shape, so Rhea's people go straight to
   * "sign-in / licence / price book" too. If either desk grows a second area
   * this reverts on its own, because it is a count and not a special case. */
  const onlyCategory = (deskId) => {
    const cats = deskId ? categoriesForDesk(deskId) : [];
    return cats.length === 1 ? cats[0].id : null;
  };

  if (!type) {
    const desk = deskFor(categoryId);
    return { deskId: desk, categoryId: categoryId || onlyCategory(desk), requestTypeId: null };
  }
  if (categoryId && type.categoryId !== categoryId) {
    return { deskId: deskFor(categoryId), categoryId, requestTypeId: null };
  }
  // A valid type implies its category, so `/new?type=st-signin` alone works.
  return { deskId: deskFor(type.categoryId), categoryId: type.categoryId, requestTypeId: type.id };
}

export default function NewTicket() {
  const { me, reference } = useDemo();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Read once, at mount. Later changes to the query string are ignored on
  // purpose — re-seeding mid-form would throw away answers already typed.
  const initial = useMemo(() => fromDeepLink(params), [params]);
  /* A FOLLOW-UP (S07b): "Start a new ticket" on a closed ticket opens this page with
   * `?followUpOf=TICK-n`, and `&for=` when the person only raised the old ticket for someone (a
   * manager): that person is filled in as who it is for, and can be changed (Grant, 2026-10-23).
   * Read from the link; `navigate('/new')` clears them. The server checks both again. */
  const followUpOf = useMemo(() => ticketNoOf(params.get('followUpOf')), [params]);
  const followFor = useMemo(() => {
    const v = params.get('for');
    return v && v.includes('@') ? v.toLowerCase() : '';
  }, [params]);
  const [deskId, setDeskId] = useState(initial.deskId);
  const [categoryId, setCategoryId] = useState(initial.categoryId);
  const [requestTypeId, setRequestTypeId] = useState(initial.requestTypeId);
  const [answers, setAnswers] = useState({});
  const [attachments, setAttachments] = useState([]);
  // Files for a form that files for real (S09). Each keeps its own retry key until it is sent.
  const [picks, setPicks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const draftRef = useRef(null);
  if (draftRef.current === null) draftRef.current = newDraft();
  const [created, setCreated] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // "Who is this for?" - '' is me. The list is everyone below me in Paylocity, from the server.
  const [team, setTeam] = useState([]);
  const [forUpn, setForUpn] = useState('');
  // The follow-up's person is filled in ONCE: a manager who changes it is not overruled later.
  const followFilled = useRef(false);
  const [followMissing, setFollowMissing] = useState(false);

  const step = created ? 3 : !categoryId ? 0 : !requestTypeId ? 1 : 2;
  // Only a form that files for real (every form, outside a walkthrough) can carry the link or files.
  const linksFollowUp = Boolean(followUpOf && filesForReal(typeById(requestTypeId)));
  const linksFiles = filesForReal(typeById(requestTypeId));
  const desk = deskById(deskId);
  const category = categoryById(categoryId);
  const type = typeById(requestTypeId);
  const fields = useMemo(() => (requestTypeId ? fieldsFor(requestTypeId) : []), [requestTypeId]);
  const offerTeam = offerForSomeone(type, team);

  /* The team is asked for only on a form that allows "for someone else" and files for real. A new
   * form always starts as "me": a person picked on one form is never carried to the next. */
  useEffect(() => {
    setForUpn('');
    setTeam([]);
    setPicks([]);
    if (!type?.forSomeoneElse || !filesForReal(type)) return undefined;
    const ctl = new AbortController();
    myTeam({ signal: ctl.signal }).then((people) => {
      if (ctl.signal.aborted) return;
      setTeam(people);
      if (!followFor || followFilled.current) return;
      followFilled.current = true;
      // Addresses are compared without case: the directory and the team list may spell them differently.
      const match = people.find((p) => String(p.upn).toLowerCase() === followFor);
      if (match) setForUpn(match.upn);
      else setFollowMissing(true);
    });
    return () => ctl.abort();
  }, [type, followFor]);

  // Scrolling to the top on each step is the difference between a wizard that
  // works on a phone and one where step 3 opens halfway down.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  /* True when this desk has exactly one area, so the category step was skipped
   * on the way in and must be skipped on the way back out too. */
  const soleArea = Boolean(deskId) && categoriesForDesk(deskId).length === 1;

  function backFromTypes() {
    if (soleArea) setDeskId(null);
    setCategoryId(null);
    setRequestTypeId(null);
  }

  /* The top Back button: one step back, the way the wizard came in. From the
   * form it returns to the request types; from the types to the areas (or the
   * desk list, when the area step was skipped); from a desk's areas to the
   * desk list; from the desk list to Home. */
  function stepBack() {
    if (requestTypeId) setRequestTypeId(null);
    else if (categoryId) backFromTypes();
    else if (deskId) setDeskId(null);
    else navigate('/');
  }

  /* Picking a category also pins its desk. Reached from the grouped
   * browse-everything list, that is what carries you INTO a desk without a
   * separate step; reached from inside a desk it is a no-op. Either way the
   * ticket and the breadcrumb agree about which desk it belongs to. */
  function pickCategory(c) {
    setDeskId(c.deskId);
    setCategoryId(c.id);
    setRequestTypeId(null);
  }

  /* A HIDDEN FIELD IS NOT AN UNANSWERED FIELD. `visibleFields` applies the
   * catalogue's showIf rules; validating against raw `fields` instead would
   * refuse to submit and then point at a question nobody can see. It is also
   * what gets RENDERED, so the two can never disagree.
   *
   * hasAnswer() rather than a falsy test, because a multi-select's empty state
   * is `[]`, and `[]` is truthy. */
  const shown = visibleFields(fields, answers);
  const missing = shown.filter((f) => f.required && !hasAnswer(answers[f.key]));

  async function submit() {
    // "Someone on my team" with nobody chosen: stop here. Never fall back to "me" or to a guess.
    const choosing = offerTeam && forUpn !== '' && !personFor(team, forUpn);
    if (missing.length || choosing) {
      setShowErrors(true);
      const first = missing.length ? document.getElementById(`f-${missing[0].key}`) : null;
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    const { note, ...formData } = answers;
    /* ONE KEY PER DRAFT. If this attempt fails and the person presses Send again
     * with the same answers, the same key goes out and the server hands back the
     * ticket it already made, not a second one. Changed answers get a new key. */
    const forWhom = offerTeam && personFor(team, forUpn) ? forUpn : '';
    const attempt = keyForAttempt(draftRef.current, { requestTypeId, formData, note: note || '', forUpn: forWhom });
    draftRef.current = attempt.draft;
    try {
      const ticket = await createTicket({
        categoryId,
        requestTypeId,
        formData,
        note: note || '',
        attachments,
        requester: me,
        forUpn: forWhom || undefined,
        operationKey: attempt.key,
        followUpOf: linksFollowUp ? followUpOf : undefined,
      });
      draftRef.current = newDraft();
      /* The files go AFTER the ticket is filed, each naming it (S09). A file that does not arrive
       * does not un-file the ticket: the receipt says which one, so it can be added in a reply.
       * A Submit pressed again after a lost answer sends the same files with the same keys, and the
       * server keeps one copy of each. */
      const files = linksFiles && picks.length ? await sendFiles(ticket.number, picks) : null;
      setCreated(files ? { ...ticket, files } : ticket);
    } catch (error) {
      /* Only a server-backed type can get here - the prototype store cannot
       * fail. Showing the receipt anyway would hand somebody a ticket number
       * for a request that was never filed, which is the worse outcome by far. */
      setSubmitError(error?.code === 'WALKTHROUGH_WRITE_BLOCKED'
        ? error.message
        : wordsForFilingRefusal(error) || `We could not confirm that this reached IT. Look in My tickets before you send it again. If it is not there, press Submit again.${
          // The files go only after a confirmed filing, so a ticket that did arrive has none (review of #111, N1).
          linksFiles && picks.length ? ' If it is there, your files were not sent: add them in a reply on it.' : ''}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (created) return <Submitted ticket={created} onAnother={() => navigate('/new')} />;

  return (
    <>
      <section className="hero" style={{ paddingBottom: 0 }}>
        <BackButton onClick={stepBack} />
        <div className="crumb">
          <span className="material-symbols-outlined" aria-hidden="true">home</span>
          <Link to="/">Home</Link>
          <span>›</span>
          {step === 0 && !deskId ? <span>New request</span> : <button type="button" className="crumb__link" onClick={() => { setDeskId(null); setCategoryId(null); setRequestTypeId(null); }} style={crumbBtn}>New request</button>}
          {/* The desk sits between Home and the category, so it is always clear
              WHICH help desk a ticket is being raised with. Clicking it drops
              back to that desk's areas, not all the way to the start. */}
          {desk ? (
            <>
              <span>›</span>
              {step === 0 ? <span>{desk.label}</span> : <button type="button" className="crumb__link" onClick={backFromTypes} style={crumbBtn}>{desk.label}</button>}
            </>
          ) : null}
          {category ? (
            <>
              <span>›</span>
              {step === 1 ? <span>{category.label}</span> : <button type="button" onClick={() => setRequestTypeId(null)} style={crumbBtn}>{category.label}</button>}
            </>
          ) : null}
          {type ? (
            <>
              <span>›</span>
              <span>{type.label}</span>
            </>
          ) : null}
        </div>

        <h1 className="hero__title" style={{ fontSize: 32 }}>
          {step === 0 ? (
            desk ? <>{desk.label}</> : <>What do you <strong>need?</strong></>
          ) : step === 1 ? (
            category.label
          ) : (
            type.label
          )}
        </h1>
        <p className="hero__sub">
          {step === 0
            ? desk
              ? desk.hint
              : 'Pick the closest one. Every route ends in a short form — nothing here is a blank box.'
            : step === 1
              /* Two problems this line used to have, both caught by looking at
                 the AI desk rather than by a test. `.toLowerCase()` turned "AI
                 tools" into "ai tools", and "IT has had" was a volume claim —
                 true of Phones, a lie on a desk that has never had a ticket.
                 A category marked `new` gets an honest line instead. */
              ? category.share === 'new'
                ? `${typesFor(categoryId).length} to start with. This desk is new — if none of them fit, say so and we will add one.`
                : `${typesFor(categoryId).length} things cover nearly every ticket we have had here.`
              : 'Everything IT asks you anyway — answered before they have to.'}
        </p>
      </section>

      {followUpOf ? (
        <div className={`banner${linksFollowUp || !requestTypeId ? ' banner--info' : ''}`} role="note" style={{ marginTop: 14 }}>
          <span className="material-symbols-outlined" aria-hidden="true">link</span>
          <span>
            {linksFollowUp || !requestTypeId
              ? `This new ticket follows up ${followUpOf}. IT will see the two tickets linked.`
              : `This form is only a demo, so it will not be linked to ${followUpOf}.`}
            {followMissing
              ? ` The person ${followUpOf} was raised for is not on your team list, so it is not filled in. Unless you pick them under "Who is this for?", it will be filed for you.`
              : ''}
          </span>
        </div>
      ) : null}

      {step === 0 ? (
        /* Two shapes, one step.
         *
         * Arriving from a Home tile (`?desk=`), you see only that desk's areas —
         * the desk has already been chosen and re-offering it would be a tap for
         * nothing. Arriving at bare `/new` ("Browse all"), you see EVERY area
         * grouped under its desk heading.
         *
         * Grouping rather than adding a desk-picking step is deliberate: the
         * wizard's pitch is "five taps", and someone who already knows they want
         * Phones should not have to pick IT Help first to get there. The desk is
         * still visible, as a heading, so nobody loses track of where a ticket
         * is going. */
        deskId ? (
          <>
            <div className="grid" style={{ marginTop: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              {categoriesForDesk(deskId).map((c) => (
                <CategoryTile key={c.id} c={c} onPick={pickCategory} />
              ))}
            </div>
            <div className="inline" style={{ marginTop: 14 }}>
              <button type="button" className="btn btn--ghost" onClick={() => setDeskId(null)}>
                <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
                A different desk
              </button>
            </div>
          </>
        ) : (
          DESKS.map((d) => (
            <section className="section" key={d.id}>
              <div className="section__head">
                <h2 className="section__title">{d.label}</h2>
                <span className="section__note">{d.blurb}</span>
              </div>
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                {categoriesForDesk(d.id).map((c) => (
                  <CategoryTile key={c.id} c={c} onPick={pickCategory} />
                ))}
              </div>
            </section>
          ))
        )
      ) : null}

      {step === 1 ? (
        <>
          <div className="list" style={{ marginTop: 20 }}>
            {typesFor(categoryId).map((t) => (
              <button
                key={t.id}
                type="button"
                className="li"
                onClick={() => {
                  setRequestTypeId(t.id);
                  setAnswers({});
                  setShowErrors(false);
                }}
              >
                <span className="material-symbols-outlined" aria-hidden="true">{t.icon}</span>
                <span className="li__main">
                  <b>{t.label}</b>
                  <small>{t.blurb}</small>
                </span>
                <span className="material-symbols-outlined li__go" aria-hidden="true">chevron_right</span>
              </button>
            ))}
          </div>
          <div className="inline" style={{ marginTop: 14 }}>
            {/* On a desk with only one area, "Different area" would drop you
                on a single tile with nothing to choose — the dead end the AI
                desk screenshot exposed. There, back means a different DESK. */}
            <button type="button" className="btn btn--ghost" onClick={backFromTypes}>
              <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
              {soleArea ? 'A different desk' : 'Different area'}
            </button>
            <span className="muted" style={{ fontSize: 11.5 }}>
              Nothing here fits? Pick the closest — IT can move it.
            </span>
          </div>
        </>
      ) : null}

      {/* A conversational type does not have a form to render. The bot asks the
          questions instead and fills the SAME field keys, so everything after
          intake - priority, the queue, reporting - is unchanged. Driven off the
          catalogue, not a hard-coded id: adding a second conversational type is
          a seed change, never an edit here. */}
      {step === 2 && type?.intakeMode === 'conversation' ? (
        <ClaudeSeatChat onBack={() => setRequestTypeId(null)} />
      ) : null}

      {/* ONE QUESTION AT A TIME, for the types that ask for it. Driven off the
          catalogue rather than an id, so the day a second long form appears it
          is a seed change and not an edit here. Everything else keeps the
          single-page layout Grant's walkthrough scripts are written against. */}
      {step === 2 && type?.intakeMode !== 'conversation' && type?.flow === 'stepper' ? (
        <FormWizard
          type={type}
          fields={fields}
          answers={answers}
          setAnswers={setAnswers}
          submitting={submitting}
          submitError={submitError}
          onSubmit={submit}
          onBack={() => setRequestTypeId(null)}
        />
      ) : null}

      {step === 2 && type?.intakeMode !== 'conversation' && type?.flow !== 'stepper' ? (
        <Form
          fields={fields}
          shown={shown}
          answers={answers}
          setAnswers={setAnswers}
          attachments={attachments}
          setAttachments={setAttachments}
          picks={picks}
          setPicks={setPicks}
          showErrors={showErrors}
          submitError={submitError}
          missing={missing}
          me={me}
          team={offerTeam ? team : []}
          forUpn={forUpn}
          setForUpn={setForUpn}
          reference={reference}
          categoryId={categoryId}
          requestTypeId={requestTypeId}
          submitting={submitting}
          onSubmit={submit}
          onBack={() => setRequestTypeId(null)}
        />
      ) : null}
    </>
  );
}

const crumbBtn = {
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  color: 'var(--ihs-mid-blue)',
  cursor: 'pointer',
};

/* `shown` is the VISIBLE fields; `fields` is every field the type declares.
 * Both are passed and they are NOT interchangeable:
 *   - `shown`  renders, and is what the required-check runs over.
 *   - `fields` is only for finding a field by flag (the manager prefill), which
 *     must still find one that a showIf is currently hiding.
 * `shown` is computed in the parent because the parent needs `missing` out of
 * it; computing it twice is how a form and its validation drift apart.
 *
 * IT IS A PROP, NOT A CLOSURE. Shipped once (#22) as a bare `shown` inside this
 * function, which is a ReferenceError the moment <Form> renders - and <Form> is
 * every intake form in the app, not just the one that was being worked on. The
 * whole page went blank. Lint does not catch it, the unit tests do not render,
 * and `npm run smoke` - which does, and does catch it - was not run. */
function Form({
  fields, shown, answers, setAnswers, attachments, setAttachments, picks, setPicks, showErrors, submitError,
  missing, me, team, forUpn, setForUpn, reference, categoryId, requestTypeId, submitting, onSubmit, onBack,
}) {
  /* Recomputed on every answer. Pure and synchronous — no request, no model.
   * Walkthrough build only: the guess runs the demo rules in this browser, not the rules the server
   * files with, so on the live app it could name the wrong priority (S08 part B). The live form shows
   * no guess; the ticket gets its priority when it is filed. */
  const { walkthrough } = useDemo();
  const verdict = useMemo(
    () => (walkthrough ? previewPriority({ categoryId, requestTypeId, formData: answers, requester: me }) : null),
    [walkthrough, categoryId, requestTypeId, answers, me]
  );
  const policy = verdict && reference?.sla.find((p) => p.priority === verdict.priority);
  const ready = missing.length === 0;

  /* ---------------------------------------------------------- manager fill
   *
   * "Who is your manager?" prefills from the signed-in requester's own manager.
   * `user.id` is the Entra object id — SWA's `userId` is the oid for the aad
   * provider (the same assumption `api/src/shared/accessMap.js` relies on).
   *
   * Entra knows the reporting line for about 1 IHS account in 40 (21 of 844,
   * measured 2026-08-17), so a null answer is the NORMAL outcome: nothing
   * happens, nothing spins, and the box stays an ordinary lookup. Only the
   * `fromMe` field opts in — "which manager approved this?" on the phones form
   * is not necessarily the requester's own manager, so it is left alone.
   *
   * Never overwrites an answer already given.
   */
  const { user } = useAuth();
  const managerField = fields.find((f) => f.isManager && f.fromMe);

  useEffect(() => {
    if (!managerField || !user?.id) return undefined;
    let cancelled = false;
    managerOf(user.id).then((mgr) => {
      if (cancelled || !mgr) return;
      setAnswers((a) => (a[managerField.key] ? a : { ...a, [managerField.key]: mgr }));
    });
    return () => {
      cancelled = true;
    };
  }, [managerField, user, setAnswers]);

  return (
    <>
      <div className="card card--pad" style={{ marginTop: 20 }}>
        {/* The facts joined to the answers, shown rather than assumed. In the
            real app these come from Entra plus the HR data in Snowflake. */}
        <div className="field">
          <span className="field__label">You</span>
          <div className="pre">
            <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
            {me.name} · {me.role} · {me.brand}
            <em>from your profile</em>
          </div>
        </div>

        {team.length ? <ForWhom team={team} forUpn={forUpn} setForUpn={setForUpn} showError={showErrors} /> : null}

        {shown.map((f) => (
          <Field
            key={f.key}
            field={f}
            value={answers[f.key]}
            invalid={showErrors && f.required && !hasAnswer(answers[f.key])}
            onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}
          />
        ))}

        {/* A form that files for real sends its files to the ticket after it is filed (S09). In a
            walkthrough build the forms keep the old picture box, which only lives in this browser. */}
        {filesForReal(typeById(requestTypeId)) ? (
          <FilePicker picks={picks} onChange={setPicks} disabled={submitting} />
        ) : (
          <Attachments items={attachments} onChange={setAttachments} />
        )}

        {verdict ? <div className={`calc${ready ? '' : ' calc--pending'}`}>
          {ready ? (
            <>
              <b>
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 15 }}>bolt</span>
                Priority {verdict.priority}
                {policy ? ` · first response within ${formatTarget(policy.responseMins, policy.aroundClock)}` : ''}
              </b>
              Rule <code>{verdict.ruleName || 'default'}</code> matched. No text was read, no model
              was called.
              {verdict.similarCount > 1
                ? ` ${verdict.similarCount} people have reported this in the last hour, so it is being treated as one problem.`
                : ''}
            </>
          ) : (
            <>
              <b>
                <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 15 }}>bolt</span>
                Priority decides itself
              </b>
              Answer the questions above and the priority — and the rule that set it — appear here
              before you submit.
            </>
          )}
        </div> : null}

        <div className="inline" style={{ marginTop: 13 }}>
          <button type="button" className="btn btn--primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Submit'}
          </button>
          <span className="muted" style={{ fontSize: 11 }}>Goes straight to the queue</span>
          <button type="button" className="btn btn--ghost" onClick={onBack} style={{ marginLeft: 'auto' }}>
            Back
          </button>
        </div>
      </div>

      {submitError ? (
        <div className="banner" style={{ marginTop: 14 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>{submitError}</span>
        </div>
      ) : null}

      {showErrors && missing.length ? (
        <div className="banner" style={{ marginTop: 14 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>
            {missing.length === 1
              ? 'One question still needs an answer.'
              : `${missing.length} questions still need an answer.`}
          </span>
        </div>
      ) : null}
    </>
  );
}


/* "Who is this for?" - only for a manager, only on a form that allows it (lib/onBehalf.js).
 * The list is everyone below them in Paylocity. The server checks it again when it is sent. */
const PICK = 'pick'; // "Someone on my team" pressed, nobody chosen yet

function ForWhom({ team, forUpn, setForUpn, showError }) {
  const picked = personFor(team, forUpn);
  const someone = forUpn !== '';
  const seen = new Set();
  const sameName = new Set(team.filter((p) => (seen.has(p.name) ? true : (seen.add(p.name), false))).map((p) => p.name));
  return (
    <div className="field">
      <span className="field__label" id="for-whom-label">Who is this for?</span>
      <div className="inline" role="group" aria-labelledby="for-whom-label" style={{ flexWrap: 'wrap', gap: 6 }}>
        <button type="button" className={`o${someone ? '' : ' o--on'}`} aria-pressed={!someone} onClick={() => setForUpn('')}>
          Me
        </button>
        <button type="button" className={`o${someone ? ' o--on' : ''}`} aria-pressed={someone} onClick={() => setForUpn(picked ? picked.upn : PICK)}>
          Someone on my team
        </button>
      </div>
      {someone ? (
        <>
          <label style={{ display: 'block', marginTop: 8 }}>
            <span className="field__label">Person</span>
            {/* Nobody is chosen for you: a manager who presses Submit without picking is stopped,
                rather than the ticket going to whoever sorts first. Names only; an address is shown
                only to tell two people with the same name apart. */}
            <select value={picked ? forUpn : ''} onChange={(e) => setForUpn(e.target.value || PICK)} aria-invalid={showError || undefined}>
              <option value="">Choose a person…</option>
              {team.map((p) => <option key={p.upn} value={p.upn}>{sameName.has(p.name) ? `${p.name} (${p.upn})` : p.name}</option>)}
            </select>
          </label>
          {showError && !picked ? <p className="why" role="alert">Choose who this is for.</p> : null}
          <p className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
            {picked ? `The ticket will be ${picked.name}'s. ` : ''}You and they can both see it and reply. The list is
            everyone below you in Paylocity.
          </p>
        </>
      ) : null}
    </div>
  );
}

/* Attachments at intake.
 *
 * A screenshot of the error arriving WITH the problem is the single most useful
 * thing on an IT ticket, which is why this is on the intake form rather than
 * bolted on later.
 *
 * The prototype downscales to 1400px / JPEG 0.72 before storing. That is not
 * cosmetic — raw phone screenshots as base64 blow localStorage's ~5MB quota on
 * the second upload and the demo silently stops persisting. The real build
 * uploads straight to blob storage with a short-lived SAS and never does this.
 */
function Attachments({ items, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function onFiles(fileList) {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    setBusy(true);
    const shrunk = await Promise.all(files.slice(0, 4 - items.length).map(downscale));
    onChange([...items, ...shrunk.filter(Boolean)]);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="field">
      <span className="field__label">
        A photo or screenshot <span className="opt">(optional)</span>
      </span>

      <button type="button" className="dropzone" onClick={() => inputRef.current?.click()} disabled={busy || items.length >= 4}>
        <span className="material-symbols-outlined" aria-hidden="true">add_photo_alternate</span>
        {busy ? 'Adding…' : items.length >= 4 ? 'That is four — plenty' : 'Take a photo or choose an image'}
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />

      <span className="field__hint">Usually saves a round trip. Up to four.</span>

      {items.length ? (
        <div className="thumbs">
          {items.map((a) => (
            <div key={a.id} className="thumb">
              <img src={a.dataUrl} alt={a.name} />
              <button
                type="button"
                className="thumb__x"
                aria-label={`Remove ${a.name}`}
                onClick={() => onChange(items.filter((x) => x.id !== a.id))}
              >
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function downscale(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const max = 1400;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({
          id: `a-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
          name: file.name,
          source: 'intake',
          dataUrl: canvas.toDataURL('image/jpeg', 0.72),
        });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/* One category tile, shared by both shapes of step 0 — the single-desk grid and
 * the grouped browse-everything list. Extracted rather than copied, the same
 * call made for OpenRequests when the technician layout landed: a change to how
 * a category reads must happen in one place, or the two lists drift. */
function CategoryTile({ c, onPick }) {
  return (
    <button
      type="button"
      className={`tile${c.hot ? ' tile--hot' : ''}`}
      onClick={() => onPick(c)}
    >
      <span className="material-symbols-outlined" aria-hidden="true">{c.icon}</span>
      <b>{c.label}</b>
      <small>{c.blurb}</small>
      {/* The observed share of the last 100 real tickets. It is here so the
          person choosing can see which door most people go through. */}
      {c.share !== '—' ? <span className="vol">{c.share}</span> : null}
    </button>
  );
}

/* The receipt.
 *
 * IT'S DONE. That is the only thing this screen has to land, so it says it in
 * four words and nothing competes with them. Three things used to get in the
 * way and all three are gone:
 *
 *   - "That's with IT." Ryan, 2026-09-11: *"confusing"*. It reads as an
 *     opinion about the request rather than a statement that it was sent.
 *   - The priority chip and the rule that set it. A priority is IT's triage
 *     vocabulary; to the person who just filled in the form it is a grade they
 *     did not ask for and cannot change.
 *   - "Open the ticket", on a button that only VIEWS it. Somebody who has just
 *     pressed send reads that as a second step they still have to take.
 */
function Submitted({ ticket, onAnother }) {
  const fields = fieldsFor(ticket.requestTypeId);

  return (
    <>
      <section className="hero">
        <p className="hero__eyebrow">
          <span className="ref">{ticket.number}</span>
        </p>
        <h1 className="hero__title" style={{ fontSize: 34 }}>
          IT has <strong>your request.</strong>
        </h1>
        <p className="hero__sub">
          Nothing else is needed from you. You'll get an email when someone replies, and the
          whole conversation stays on the ticket.
        </p>
      </section>

      <div className="card card--float" style={{ marginTop: 18 }}>
        {/* The server filed it but could not save the link (017 only part-run): the ticket is real,
            so this is not an error about the ticket. It says what the person can do about it. */}
        {ticket.followUpOf ? (
          <div className={`banner ${ticket.followUpLinked ? 'banner--info' : 'banner--bad'}`} role={ticket.followUpLinked ? 'note' : 'alert'} style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined" aria-hidden="true">{ticket.followUpLinked ? 'link' : 'link_off'}</span>
            <span>
              {ticket.followUpLinked
                ? `Linked to ${ticket.followUpOf} as a follow-up.`
                : `Your ticket is filed, but the link to ${ticket.followUpOf} was not saved. Write "follow-up of ${ticket.followUpOf}" in a reply on this ticket, so IT can find the old one.`}
            </span>
          </div>
        ) : null}
        {ticket.files && ticket.files.failed.length ? (
          <div className="banner banner--bad" role="alert" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined" aria-hidden="true">attach_file</span>
            <span>{wordsForFailed(ticket.files.failed, { where: 'Your ticket' })}</span>
          </div>
        ) : null}
        {ticket.similarCount > 1 ? (
          <div className="banner banner--info" style={{ marginTop: 12 }}>
            <span className="material-symbols-outlined" aria-hidden="true">groups</span>
            <span>
              {ticket.similarCount} people have reported this in the last hour. IT is treating it
              as one problem, not {ticket.similarCount} separate tickets.
            </span>
          </div>
        ) : null}

        <div style={{ marginTop: 18 }}>
          <h2 className="section__title" style={{ marginBottom: 8 }}>What you told us</h2>
          <div className="tablewrap">
            <table className="table">
              <tbody>
                {/* `answerRows` and NOT Object.entries: the payload comes back
                    from a Snowflake VARIANT, which sorts its keys, so reading
                    the payload's own order showed the questions alphabetically
                    rather than in the order they were asked. */}
                {answerRows(fields, ticket.formData).map(({ key, field, value }) => (
                  <tr key={key}>
                    <td style={{ color: 'var(--ihs-ink-3)', width: '45%' }}>{field?.label || key}</td>
                    <td>{labelFor(field, value)}</td>
                  </tr>
                ))}
                {ticket.note ? (
                  <tr>
                    <td style={{ color: 'var(--ihs-ink-3)' }}>Your note</td>
                    <td>{ticket.note}</td>
                  </tr>
                ) : null}
                {ticket.files ? (
                  <tr>
                    <td style={{ color: 'var(--ihs-ink-3)' }}>Files</td>
                    <td>{ticket.files.saved.length} added</td>
                  </tr>
                ) : null}
                {ticket.attachments.length ? (
                  <tr>
                    <td style={{ color: 'var(--ihs-ink-3)' }}>Images</td>
                    <td>{ticket.attachments.length} attached</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="inline" style={{ marginTop: 16 }}>
        <Link to={`/ticket/${ticket.number}`} className="btn btn--primary btn--big">
          View ticket
        </Link>
        <button type="button" className="btn btn--quiet" onClick={onAnother}>
          Raise another
        </button>
      </div>
    </>
  );
}
